import { create } from 'zustand'
import { env } from '@/config/env'

// Polls GET /ping (plain "pong") every 30 s online, 8 s offline. The first failure shows a banner; offline 8 s or more
// (in practice the second failure) shows the blocking overlay. `navigator.onLine` and tab visibility are fast paths, never the truth.
export type ConnectivityStatus = 'online' | 'offlineBanner' | 'offlineOverlay'

export const ONLINE_POLL_MS = 30_000
export const OFFLINE_POLL_MS = 8_000
export const OVERLAY_AFTER_MS = 8_000
export const PING_TIMEOUT_MS = 5_000

type Listener = () => void

interface ConnectivityState {
  status: ConnectivityStatus
  offlineSince: number | null
  start: () => void
  stop: () => void
  check: () => Promise<boolean>
  onRestored: (cb: Listener) => () => void
}

let timer: number | undefined
let listeners = new Set<Listener>()
let pinging: Promise<boolean> | null = null

/** Any status below 500 counts as reachable (a 429 still proves the server is there). */
export async function ping(): Promise<boolean> {
  try {
    const res = await fetch(`${env.apiBaseUrl}/ping`, { method: 'GET', cache: 'no-store', signal: AbortSignal.timeout(PING_TIMEOUT_MS) })
    return res.status < 500
  } catch {
    return false
  }
}

export const useConnectivityStore = create<ConnectivityState>((set, get) => {
  const schedule = () => {
    window.clearTimeout(timer)
    timer = window.setTimeout(() => void get().check(), get().status === 'online' ? ONLINE_POLL_MS : OFFLINE_POLL_MS)
  }
  const apply = (reachable: boolean) => {
    const { status, offlineSince } = get()
    if (reachable) {
      if (status !== 'online') {
        set({ status: 'online', offlineSince: null })
        listeners.forEach((l) => l())
      }
      return
    }
    const since = offlineSince ?? Date.now()
    const overlay = Date.now() - since >= OVERLAY_AFTER_MS
    set({ status: overlay ? 'offlineOverlay' : 'offlineBanner', offlineSince: since })
  }
  return {
    status: 'online',
    offlineSince: null,
    check: () => {
      if (!pinging) {
        pinging = ping()
          .then((ok) => {
            apply(ok)
            return ok
          })
          .finally(() => {
            pinging = null
            if (timer !== undefined) schedule()
          })
      }
      return pinging
    },
    start: () => {
      if (timer !== undefined) return
      timer = window.setTimeout(() => void get().check(), ONLINE_POLL_MS)
      window.addEventListener('online', onOnline)
      window.addEventListener('offline', onOffline)
      document.addEventListener('visibilitychange', onVisible)
    },
    stop: () => {
      window.clearTimeout(timer)
      timer = undefined
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
      document.removeEventListener('visibilitychange', onVisible)
      listeners = new Set()
    },
    onRestored: (cb) => {
      listeners.add(cb)
      return () => listeners.delete(cb)
    },
  }
})

function onOnline() {
  // Optimistic, like the mobile app on resume: show online, confirm with a ping.
  useConnectivityStore.setState({ status: 'online', offlineSince: null })
  void useConnectivityStore.getState().check()
}
function onOffline() {
  useConnectivityStore.setState({ status: 'offlineBanner', offlineSince: Date.now() })
  void useConnectivityStore.getState().check()
}
function onVisible() {
  if (document.visibilityState === 'visible') void useConnectivityStore.getState().check()
}

export function useOnRestored(cb: Listener) {
  useConnectivityStore.getState().onRestored(cb)
}

/** "12s" / "1m 5s" */
export function formatOffline(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000))
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`
}
